import React, { useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { DropdownSingle, DropdownMulti, colors2, UI_BODY_SM_DF, DateRangePicker } from "oolib";
import { ButtonPrimary, ButtonSecondary } from "oolib/dist/v2/components/Buttons";
import { TagClear } from "oolib/dist/v2/components/Tags";
import { CustomSelectComp } from "@/components/analysisModules/AnnotationExplorer/comps/CustomSelectComp";
import {
  InterfaceContainer,
  InterfaceHeader,
  InterfaceDescription,
  InterfaceActions,
  HelpText,
  ConnectedBanner,
  ConnectedMeta,
  StaleBanner,
  ErrorBanner,
  EmptyBanner,
} from "./BaseIntegrationInterface";
import { useOAuthConnection } from "../../hooks/useOAuthConnection";
import { IntegrationDisconnectConfirmModal } from "@/_self_managed/SM_Integrations/IntegrationDisconnectConfirmModal";

// Zoho Desk uses OAuth with refresh tokens (fits the framework cleanly), with
// one quirk: the auth + API endpoints are per data center. The customer picks
// their region (US/EU/AU/IN/JP/CN) BEFORE the OAuth redirect; we ship it as
// providerParams.region and the BE round-trips it through OAuth state. After
// OAuth, providerAccountId is stored as "<orgId>@<region>" so both refresh
// and adapter calls can recover the DC without extra schema fields.

// ============================================
// ZOHO DESK ICON
// ============================================

export const ZohoDeskIcon = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="#E42527" />
    <path
      fill="#FFFFFF"
      d="M9 9h14l-9 11h9v3H9l9-11H9z"
    />
  </svg>
);

// ============================================
// CONSTANTS
// ============================================

const REGION_OPTIONS = [
  { display: "United States (zoho.com)",        value: "us" },
  { display: "Europe (zoho.eu)",                value: "eu" },
  { display: "Australia (zoho.com.au)",         value: "au" },
  { display: "India (zoho.in)",                 value: "in" },
  { display: "Japan (zoho.jp)",                 value: "jp" },
  { display: "China (zoho.com.cn)",             value: "cn" },
];

const STATUS_OPTIONS = [
  { display: "Open",       value: "Open" },
  { display: "On Hold",    value: "On Hold" },
  { display: "Escalated",  value: "Escalated" },
  { display: "Closed",     value: "Closed" },
];

const DAY_MS = 24 * 60 * 60 * 1000;
const SOFT_WARN_DAYS = 90;
const HARD_CAP_DAYS = 365;

const isoDaysAgo = (n) => new Date(Date.now() - n * DAY_MS).toISOString().slice(0, 10);
const todayIso = () => new Date().toISOString().slice(0, 10);

// ============================================
// STYLED COMPONENTS
// ============================================

const FieldGroup = styled.div`
  margin-top: 16px;
`;

const DateRangeRow = styled.div`
  margin-top: 16px;
`;

const RangeHint = styled(UI_BODY_SM_DF)`
  color: ${({ $error }) => ($error ? "#c0392b" : colors2.grey60)};
  margin-top: 8px;
`;

// ============================================
// HELPERS
// ============================================

// providerAccountId is stored as "<orgId>@<region>". When the connection
// goes stale we want to silently reconnect into the same DC the user picked
// last time — so derive the region from there.
const extractRegionFromAccountId = (providerAccountId) => {
  if (!providerAccountId || typeof providerAccountId !== "string" || !providerAccountId.includes("@")) {
    return null;
  }
  const region = providerAccountId.split("@")[1]?.toLowerCase();
  return REGION_OPTIONS.some((r) => r.value === region) ? region : null;
};

const regionOptionByValue = (value) =>
  REGION_OPTIONS.find((r) => r.value === value) || null;

// ============================================
// INTERFACE
// ============================================

export const ZohoDeskInterface = ({
  adapterConfig,
  onSubmit,
  isLoading,
  submitError,
  onClearSubmitError,
}) => {
  const {
    connection,
    connectionLoading,
    connectError,
    handleConnect,
    isConnecting,
    handleDisconnect,
  } = useOAuthConnection({
    providerId: "zohoDesk",
    providerName: "Zoho Desk",
    // No preflight — for v1 we just use the org returned by /organizations.
    // If we ever add an org picker, switch this to true and fetchPreflightData.
    preflightEnabled: false,
  });

  // Pre-connect input: DC region. Default to US (most common).
  const [selectedRegion, setSelectedRegion] = useState(REGION_OPTIONS[0]);

  const [dateRange, setDateRange] = useState([isoDaysAgo(30), todayIso()]);
  const [startDate, endDate] = dateRange;
  const [statusFilter, setStatusFilter] = useState([]);

  const submitErrorMessage =
    submitError && submitError.sourceId === "zohoDesk" ? submitError.message : null;
  const isEmptyResult =
    submitErrorMessage && /no\s+(zoho\s+(desk\s+)?)?(tickets|data)\s+(found|to)/i.test(submitErrorMessage);

  const rangeDays = startDate && endDate
    ? Math.round((new Date(endDate) - new Date(startDate)) / DAY_MS)
    : 0;
  const rangeTooLong = rangeDays > HARD_CAP_DAYS;
  const rangeWarn = !rangeTooLong && rangeDays > SOFT_WARN_DAYS;
  const rangeInverted = startDate && endDate && new Date(startDate) > new Date(endDate);

  const canSubmit = connection?.connected
    && connection?.status !== "stale"
    && startDate && endDate
    && !rangeTooLong
    && !rangeInverted;

  const onConnectClick = () => {
    if (!selectedRegion?.value) return;
    handleConnect({ providerParams: { region: selectedRegion.value } });
  };

  const onReconnectClick = () => {
    // Reuse the DC the user picked originally. Falls back to the dropdown
    // selection if for some reason the stored account is malformed.
    const storedRegion = extractRegionFromAccountId(connection?.providerAccountId);
    const region = storedRegion || selectedRegion?.value;
    if (!region) return;
    handleConnect({ providerParams: { region } });
  };

  const onDisconnect = async () => {
    await handleDisconnect();
    // Don't clear local filter state — user may want to reconnect and re-run.
  };

  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const confirmDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await onDisconnect();
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectConfirm(false);
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onClearSubmitError && onClearSubmitError();
    // Anchor to local-day boundaries (same fix that landed in Notion — naive
    // YYYY-MM-DD slicing silently shifts back by the user's TZ offset).
    const createdAfter = moment(startDate).startOf("day").toISOString();
    const createdBefore = moment(endDate).endOf("day").toISOString();
    const selectedStatuses = (Array.isArray(statusFilter) ? statusFilter : [])
      .map((s) => s?.value)
      .filter(Boolean);
    onSubmit({
      adapterOptions: {
        startDate: createdAfter,
        endDate: createdBefore,
        statusFilter: selectedStatuses.length > 0 ? selectedStatuses : "all",
      },
    });
  };

  // ------- Render -------

  if (connectionLoading) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Zoho Desk</InterfaceHeader>
        <InterfaceDescription>Checking connection status…</InterfaceDescription>
      </InterfaceContainer>
    );
  }

  // State 1: not connected — collect region before redirect.
  if (!connection?.connected) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Zoho Desk</InterfaceHeader>
        <InterfaceDescription>
          Import support tickets from Zoho Desk. Pick the region your Zoho
          account lives in, then authorize Dots to read tickets from your
          help-desk portal.
        </InterfaceDescription>

        <FieldGroup>
          <DropdownSingle
            label="Your Zoho Desk region"
            placeholder="Select your data center..."
            options={REGION_OPTIONS}
            value={selectedRegion}
            onChange={(k, v) => setSelectedRegion(v)}
            lightboxHeight="small"
            popOutOfOverflowHiddenParent={true}
          />
        </FieldGroup>

        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}

        <InterfaceActions>
          <ButtonPrimary onClick={onConnectClick} disabled={!selectedRegion?.value || isConnecting} isLoading={isConnecting}>
            Connect Zoho Desk
          </ButtonPrimary>
        </InterfaceActions>

        <HelpText>
          Not sure which region you're in? Check the URL when you're logged
          into Zoho Desk — <strong>desk.zoho.com</strong> = US,{" "}
          <strong>desk.zoho.eu</strong> = Europe, etc. Dots requests read
          access to tickets, contacts, and basic account info.
        </HelpText>
      </InterfaceContainer>
    );
  }

  // State 3: stale — reconnect into the same DC.
  if (connection.status === "stale") {
    const storedRegion = extractRegionFromAccountId(connection.providerAccountId);
    const storedRegionLabel = regionOptionByValue(storedRegion)?.display || storedRegion || "your region";
    return (
      <InterfaceContainer>
        <InterfaceHeader>Reconnect Zoho Desk</InterfaceHeader>
        <StaleBanner>
          <ConnectedMeta>
            <strong>⚠ Connection needs to be renewed</strong>
            <span>
              {connection.providerAccountName
                ? `Your Zoho Desk connection (${connection.providerAccountName}, ${storedRegionLabel}) expired or was revoked.`
                : `Your Zoho Desk connection (${storedRegionLabel}) expired or was revoked.`}
            </span>
          </ConnectedMeta>
          <ButtonPrimary onClick={onReconnectClick} isLoading={isConnecting} disabled={isConnecting}>Reconnect Zoho Desk</ButtonPrimary>
        </StaleBanner>
        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
      </InterfaceContainer>
    );
  }

  // State 2: connected & ready to import.
  const storedRegion = extractRegionFromAccountId(connection.providerAccountId);
  const storedRegionLabel = regionOptionByValue(storedRegion)?.display || storedRegion;
  // providerAccountId is "<orgId>@<region>" — pretty-print just the orgId.
  const displayOrgId = connection.providerAccountId?.split("@")?.[0] || connection.providerAccountId;

  return (
    <InterfaceContainer>
      <InterfaceHeader>Import from Zoho Desk</InterfaceHeader>
      <InterfaceDescription>
        Each ticket becomes one row. The full thread (description + replies +
        private notes) is rendered as a single transcript.
      </InterfaceDescription>

      <ConnectedBanner>
        <ConnectedMeta>
          <strong>
            ✓ Connected{connection.providerAccountName ? ` to ${connection.providerAccountName}` : ""}
          </strong>
          {displayOrgId && (
            <span style={{ color: colors2.grey60 }}>
              Org {displayOrgId}{storedRegionLabel ? ` · ${storedRegionLabel}` : ""}
            </span>
          )}
          {connection.adminEmail && (
            <span style={{ color: colors2.grey60 }}>{connection.adminEmail}</span>
          )}
        </ConnectedMeta>
        <ButtonSecondary onClick={() => setShowDisconnectConfirm(true)} disabled={isLoading}>
          Disconnect
        </ButtonSecondary>
      </ConnectedBanner>

      {showDisconnectConfirm && (
        <IntegrationDisconnectConfirmModal
          name="Zoho Desk"
          onConfirm={confirmDisconnect}
          onClose={() => setShowDisconnectConfirm(false)}
          isLoading={isDisconnecting}
        />
      )}

      {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
      {submitErrorMessage && isEmptyResult && <EmptyBanner>{submitErrorMessage}</EmptyBanner>}
      {submitErrorMessage && !isEmptyResult && <ErrorBanner>{submitErrorMessage}</ErrorBanner>}

      <DateRangeRow>
        <DateRangePicker
          id="zoho-date-range"
          label="Date Range"
          value={dateRange}
          onChange={(k, v) => setDateRange(v)}
          previousDateOnly
          readOnly={isLoading}
        />
      </DateRangeRow>

      {rangeInverted && (
        <RangeHint $error>End date must be on or after start date.</RangeHint>
      )}
      {rangeTooLong && (
        <RangeHint $error>
          Please select a range of {HARD_CAP_DAYS} days or fewer.
        </RangeHint>
      )}
      {rangeWarn && (
        <RangeHint>
          Large date ranges ({rangeDays} days) may take several minutes to import.
        </RangeHint>
      )}

      <FieldGroup>
        <DropdownMulti
          S
          label="Ticket Status"
          genCustomSelectComp={() => (
            <CustomSelectComp
              placeholder="All statuses"
              display={statusFilter.length > 0 ? `${statusFilter.length} Selected` : null}
            />
          )}
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={(k, v) => setStatusFilter(v || [])}
          readOnly={isLoading}
          lightboxHeight="small"
          popOutOfOverflowHiddenParent={true}
        />
        {statusFilter.length > 0 && (
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginTop: "0.8rem" }}>
            {statusFilter.map((s) => (
              <TagClear
                key={s.value}
                grey
                id={s.value}
                display={s.display}
                size="XS"
                onClick={() => {
                  if (isLoading) return;
                  setStatusFilter((prev) => prev.filter((p) => p.value !== s.value));
                }}
              />
            ))}
          </div>
        )}
      </FieldGroup>

      <RangeHint>
        Heads up: imports are capped at the most recent 200 tickets in the
        selected range for now. If you need more, narrow the date range or
        run multiple imports.
      </RangeHint>

      <InterfaceActions>
        <ButtonPrimary
          onClick={handleSubmit}
          disabled={!canSubmit || isLoading}
          isLoading={isLoading}
        >
          {adapterConfig?.preflightConfig?.submitLabel || "Fetch Tickets"}
        </ButtonPrimary>
      </InterfaceActions>
    </InterfaceContainer>
  );
};

export const zohoDeskIntegrationMeta = {
  id: "zohoDesk",
  name: "Zoho Desk",
  description: "Import support tickets from Zoho Desk",
};

export default ZohoDeskInterface;
